import { TbClock } from 'react-icons/tb';
import { ExerciseGif } from './ExerciseGif';
import './training.css';

/**
 * `restSeconds` (a whole number of seconds) as `m:ss` — the spec's own
 * example is "1:30" for 90 seconds. Returns `null` for anything that isn't a
 * finite, non-negative number so the caller can skip rendering the stat
 * entirely (no rest configured for that exercise) instead of showing "0:00".
 */
export function formatRestSeconds(totalSeconds) {
  if (typeof totalSeconds !== 'number' || !Number.isFinite(totalSeconds) || totalSeconds < 0) return null;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.round(totalSeconds % 60);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/**
 * Mobile-first exercise tile (spec "Mi rutina"/"Entrenar"): a big GIF up top,
 * name + target, `sets × reps`, formatted rest, an optional note and an
 * optional corner badge (e.g. "Tu versión" for a substituted exercise). The
 * GIF+name+stats region is the `onOpen` trigger (opens `ExerciseDetail`); the
 * optional `action` slot (e.g. "Cambiar ejercicio") renders as a sibling
 * button, never nested inside the trigger, so clicking it never also fires
 * `onOpen` and the DOM never nests two interactive controls.
 */
export function ExerciseCard({
  exercise, sets, reps, restSeconds, note, badge, order, variant, originalExerciseName, onOpen, action,
}) {
  const name = (exercise && exercise.name) || 'Ejercicio';
  const nameEn = exercise && exercise.nameEn;
  const showNameEn = Boolean(nameEn) && nameEn !== name;
  const targetLabel = (exercise && exercise.targetLabel) || (exercise && exercise.target);
  const rest = formatRestSeconds(restSeconds);
  const canOpen = typeof onOpen === 'function';
  const TriggerTag = canOpen ? 'button' : 'div';

  return (
    <article className={`gym-ex-card${variant ? ` gym-ex-card--${variant}` : ''}`}>
      <TriggerTag
        type={canOpen ? 'button' : undefined}
        className="gym-ex-card__trigger"
        onClick={canOpen ? onOpen : undefined}
        aria-label={canOpen ? `Ver detalle de ${name}` : undefined}
      >
        <span className="gym-ex-card__media">
          <ExerciseGif exercise={exercise} size="lg" />
          {order !== undefined && order !== null && <span className="gym-ex-card__order" aria-hidden="true">{order}</span>}
          {badge && <span className="gym-ex-card__badge">{badge}</span>}
        </span>
        <span className="gym-ex-card__body">
          <span className="gym-ex-card__name">{name}</span>
          {showNameEn && <span className="gym-ex-card__name-en">{nameEn}</span>}
          {originalExerciseName && (
            <span className="gym-ex-card__original">
              Original: <strong>{originalExerciseName}</strong>
            </span>
          )}
          {targetLabel && <span className="gym-ex-card__target">{targetLabel}</span>}
          <span className="gym-ex-card__stats">
            <span className="gym-ex-card__stat gym-ex-card__stat--sets">
              {sets} × {reps}
            </span>
            {rest && (
              <span className="gym-ex-card__stat gym-ex-card__stat--rest">
                <TbClock aria-hidden="true" focusable="false" />
                {rest}
              </span>
            )}
          </span>
          {note && <span className="gym-ex-card__note">{note}</span>}
        </span>
      </TriggerTag>
      {action && <div className="gym-ex-card__action">{action}</div>}
    </article>
  );
}
